import React, { useCallback } from 'react';
import { Inline } from '@atlaskit/primitives/compiled';
import Heading from '@atlaskit/heading';
import { cssMap } from '@atlaskit/css';
import { token } from '@atlaskit/tokens';
import Link from '@atlaskit/link';
import { expVal } from '@atlassian/jira-feature-experiments';
import { defineMessages, useIntl } from '@atlassian/jira-intl';
import { useAnalyticsEvents, fireUIAnalytics } from '@atlassian/jira-product-analytics-bridge';
import TabContainer from './TabContainer.tsx';
import type { TabId } from './types.tsx';

const messages = defineMessages({
	viewAll: {
		id: 'home-project-recommendations.view-all.non-final',
		defaultMessage: 'View all projects',
		description:
			'Link text for users to navigate to the project directory page to view all projects',
	},
	viewAllGalaxia: {
		id: 'home-project-recommendations.view-all-galaxia.non-final',
		defaultMessage: 'View all spaces',
		description: 'Link text for users to navigate to the project directory page to view all spaces',
	},
	sectionRecommendedTitle: {
		id: 'home-project-recommendations.section-recommended-title.non-final',
		defaultMessage: 'Recommended projects',
		description: 'Section heading for the recommended projects tab',
	},
	sectionRecommendedTitleGalaxia: {
		id: 'home-project-recommendations.section-recommended-title-galaxia.non-final',
		defaultMessage: 'Recommended spaces',
		description: 'Section heading for the recommended projects tab',
	},
	sectionRecentTitle: {
		id: 'home-project-recommendations.section-recent-title.non-final',
		defaultMessage: 'Recent projects',
		description: 'Section heading for the recent projects tab',
	},
	sectionRecentTitleGalaxia: {
		id: 'home-project-recommendations.section-recent-title-galaxia.non-final',
		defaultMessage: 'Recent spaces',
		description: 'Section heading for the recent projects tab',
	},
});

const styles = cssMap({
	headerStyles: {
		display: 'flex',
		justifyContent: 'space-between',
	},
	tabListContainerStyles: {
		height: '32px',
		borderRadius: token('radius.small'),
		borderColor: token('color.border'),
		borderStyle: 'solid',
		borderWidth: token('border.width'),
		width: 'fit-content',
		paddingLeft: token('space.100'),
		paddingRight: token('space.100'),
	},
});

type SectionTitleProps = {
	projectsDirectoryUrl: string;
	selectedTab: TabId;
	onSetSelectedTab: (newSelectedTab: TabId) => void;
};

const SectionTitle = ({
	projectsDirectoryUrl,
	selectedTab,
	onSetSelectedTab,
}: SectionTitleProps): React.ReactElement => {
	const { formatMessage } = useIntl();
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const onLinkClick = useCallback(() => {
		const analyticsEvent = createAnalyticsEvent({ action: 'clicked', actionSubject: 'link' });

		fireUIAnalytics(analyticsEvent, 'viewAllProjectsLink');
	}, [createAnalyticsEvent]);

	const sectionRecommendedTitleGalaxia = expVal('project-terminology-refresh', 'isEnabled', false)
		? messages.sectionRecommendedTitleGalaxia
		: messages.sectionRecommendedTitle;

	const sectionRecentTitleGalaxia = expVal('project-terminology-refresh', 'isEnabled', false)
		? messages.sectionRecentTitleGalaxia
		: messages.sectionRecentTitle;

	const viewAllGalaxia = expVal('project-terminology-refresh', 'isEnabled', false)
		? messages.viewAllGalaxia
		: messages.viewAll;

	return (
		<Inline alignBlock="center" xcss={styles.headerStyles}>
			<Heading size="medium">
				{formatMessage(
					selectedTab === 'recommended'
						? sectionRecommendedTitleGalaxia
						: sectionRecentTitleGalaxia,
				)}
			</Heading>
			<Inline alignBlock="center" space="space.200">
				<TabContainer selected={selectedTab} onSetSelectedTab={onSetSelectedTab} />
				<Link href={projectsDirectoryUrl} onClick={onLinkClick}>
					{formatMessage(viewAllGalaxia)}
				</Link>
			</Inline>
		</Inline>
	);
};

export default SectionTitle;
